import React from 'react'
import DataTable from '../Shared/DataTable/DataTable.js'
import * as FieldTypes from '../Shared/FieldTypes/FieldTypes.js'
import SubmissionQAListItemDetailsPane from './SubmissionQAListItemDetailsPane.js'
import SubmissionQAListAddItemPopup from './SubmissionQAListAddItemPopup.js'

// Per-submission Q&A table — the same DataTable.js (and the same list/filter/item/add
// endpoints) the cross-submission Q&A page uses, just scoped down to one submission:
// same columns minus Country/Certificate/Submission (redundant once you're already inside
// one specific submission), and the same native multi-item "Ask AI" button
// (config.enableMultiItemAIChatPane), reused as-is rather than rebuilt.
//
// Scoping to one submission goes through DataTable.js's own defaultFilters prop, which
// funnels into the exact same filter[key]=value mechanism the Country/Certificate/etc.
// column filters already use (see SubmissionsRepository.FilterQA's "SubmissionId" branch)
// — there's no separate mechanism for a fixed, non-interactive scope like this.
const SubmissionQADataTable = ({ submissionId, preLoadQAId }) => {

    let config = {};

    config.dataUrl = "/umbraco/api/SubmissionQAList/QA";
    config.getSingleItemUrl = "/umbraco/api/SubmissionQAList/QAItem";
    config.singleItemTitle = "Q&A item";
    config.entityType = "submissionqa";
    config.entityTypeId = 11;
    config.hideBreadcrumbs = true;
    config.hideTitle = true;
    config.hideSelectAllCheckbox = true;
    config.enableMultiItemAIChatPane = true;
    config.showListTotalCount = true;
    config.listTotalCountURL = "/umbraco/api/SubmissionQAList/QACount";
    config.defaultOrderByField = "CreatedOn";
    // Same deep-link mechanism as the cross-submission list (see SubmissionQAList.js) — the
    // ?qaId= param here rides alongside the submission's own ?submissionId= on this same
    // page's URL. preLoadQAId is read client-side from that param by Submissions/
    // ItemDetailsPane.js (there's no server-side .cshtml injection for it here, since this
    // table only ever mounts once already deep inside the Submissions page, not on its own
    // fresh page load) and threaded down through SubmissionQA.js.
    config.preLoadItemId = preLoadQAId;
    config.preLoadItemUrlParamName = "qaId";
    config.dataStructure = [
        { name: "", field: "Id", width: "40px", sortable: false, isSelectItemCheckbox: true },
        { name: "Title", field: "Title", width: "20%", sortable: true, renderWith: FieldTypes.FieldType_Text },
        { name: "Description", field: "Description", width: "34%", sortable: false, renderWith: FieldTypes.FieldType_TruncatedDescription },
        { name: "PDF", field: "FileUploadedOn", width: "10%", sortable: false, renderWith: FieldTypes.FieldType_PdfIndicator, hasFilter: true },
        { name: "Date Created", field: "CreatedOn", width: "16%", sortable: true, renderWith: FieldTypes.FieldType_DateTimeMinutes_UserTimeZone },
        { name: "Last Edited", field: "UpdatedOn", width: "16%", sortable: true, renderWith: FieldTypes.FieldType_DateTimeMinutes_UserTimeZone },
    ];

    // AddItemPopup only ever receives `globalContext` from DataTable.js — this closure is
    // how the current submissionId still reaches it, to skip the picker (see
    // SubmissionQAListAddItemPopup.js's fixedSubmissionId prop).
    const AddItemPopupForThisSubmission = (props) => (
        <SubmissionQAListAddItemPopup {...props} fixedSubmissionId={submissionId} />
    );

    return (
        <DataTable
            dataTableStructure={config}
            defaultFilters={[{ key: "SubmissionId", value: submissionId }]}
            ItemDetailsPane={SubmissionQAListItemDetailsPane}
            AddItemPopup={AddItemPopupForThisSubmission}
        />
    );

};

export default SubmissionQADataTable;
